import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../atoms/Card/Card';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { JournalEntrySocial, communityEntryQueryKey } from './JournalEntrySocial';
import { getCommunityEntry } from '../../services/communityService';
import { useTranslation } from '../../i18n/useTranslation';
import './CommunityFeedItem.css';

/**
 * On the owner's journal entry page: for a public entry, where it appears
 * (Community), its likes and comments — the owner can delete comments there
 * (moderation). Private entries show nothing.
 */
export function JournalEntryCommunitySection({ entryId, visibility }: { entryId: string; visibility: 'public' | 'private' }) {
  const { t } = useTranslation();
  const isPublic = visibility === 'public';
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: communityEntryQueryKey(entryId),
    queryFn: () => getCommunityEntry(entryId),
    enabled: isPublic && Boolean(entryId),
  });
  if (!isPublic) return null;

  let body;
  if (isLoading) {
    body = <LiveRegion><Text color="muted">{t('community.entryLoading')}</Text></LiveRegion>;
  } else if (isError || !data) {
    body = <LoadErrorState title={t('community.entryLoadError')} titleLevel={3} onRetry={() => void refetch()} compact />;
  } else {
    body = <JournalEntrySocial entry={data} headingLevel={3} />;
  }

  return (
    <Section
      title={t('community.reactionsTitle')}
      description={
        <>
          {t('community.publicEntryNote')}{' '}
          <Link className="rg-community-item__link" to={`/community/${entryId}`}>{t('community.viewInCommunity')}</Link>
        </>
      }
    >
      <Card>{body}</Card>
    </Section>
  );
}

export default JournalEntryCommunitySection;
